import { Buttons, DataBrowsing } from '@platform/shared/ui';
import { MetadataTypes, ProjectTypes } from '@platform/types';
import React, { useState } from 'react';
import { useSessionContext } from '../../../../contexts/SessionContext';
import { useWorkspaceContext } from '../../../../contexts/WorkspaceContext';
import * as peApi from '../../../../pe.api';
import PanelHeader from '../../../MapSidebar/PanelHeader';
import { getShadedWorkingLayer } from './utils';

const { DataBrowser } = DataBrowsing;

interface Props {
  workingLayer: ProjectTypes.SocialExplorerWorkingLayer;
  onChange: (layer: ProjectTypes.SocialExplorerWorkingLayer) => void;
}

const SocialExplorerWorkingLayerData: React.FC<Props> = ({ onChange, workingLayer }) => {
  const { notify } = useSessionContext();
  const { activeWorkspace } = useWorkspaceContext();
  const [isDataBrowserOpen, setIsDataBrowserOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleDataChange = async (
    variableSelection: ProjectTypes.VariableSelection[],
    tableMetadata: MetadataTypes.Table,
    surveyMetadata: MetadataTypes.Survey
  ) => {
    try {
      setIsLoading(true);

      const { datasetAbbrev } = variableSelection[0];

      const shadedWorkingLayer = await getShadedWorkingLayer({
        variableSelection: variableSelection.map((x) => ({
          variableId: x.variableGuid,
          tableId: x.tableGuid,
          universeVariableId: x.universeVariableUuid,
        })),
        id: workingLayer.id,
        surveyName: surveyMetadata.name,
        datasetAbbreviation: datasetAbbrev,
        currentGeoLayerId: workingLayer.metadata.geoLayerId,
      });

      onChange(shadedWorkingLayer);
    } catch {
      // TODO: handle
      notify({ content: "Couldn't apply selection to map. Something went wrong" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex flex-col gap-1 gap-1 text-xs">
            <div>{workingLayer.name}</div>
            <div>{workingLayer.description}</div>
            <div>{workingLayer.metadata.surveyName}</div>
          </div>
          <Buttons.Secondary
            className="text-primary-600 rounded-[999px] px-3.5 text-sm shadow-none hover:bg-gray-100"
            onClick={() => setIsDataBrowserOpen(true)}
            data-rh="Change data"
          >
            Change
          </Buttons.Secondary>
        </div>
      </div>
      {isDataBrowserOpen && (
        <div className="absolute top-0 left-0 bottom-0 right-0 z-50 flex h-full w-full flex-col bg-white">
          <PanelHeader title="Change data" onBack={() => setIsDataBrowserOpen(false)} />
          <DataBrowser
            isLoading={isLoading}
            surveysApi={peApi.getSurveys}
            allowMultiSelection
            onChange={handleDataChange}
            activeWorkspaceId={activeWorkspace.id}
            selection={workingLayer.variableSelection.map((x) => ({
              variableGuid: x.variableId,
              tableGuid: x.tableId,
              isUserSurvey: workingLayer.metadata.isUserSurvey,
              datasetAbbrev: workingLayer.metadata.datasetAbbreviation,
              surveyName: workingLayer.metadata.surveyName,
              universeVariableUuid: x.universeVariableId,
            }))}
          />
        </div>
      )}
    </>
  );
};

export default SocialExplorerWorkingLayerData;
